import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MapPin, Plus, CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";

import { AppShell } from "src/components/layout/AppShell";
import { Button } from "src/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "src/components/ui/select";
import { useAuth } from "src/contexts/AuthContext";
import { useToast } from "src/components/ui/toast";
import { fetchCalendar } from "src/features/site_visits/api/siteVisitsApi";
import { ScheduleVisitModal } from "src/features/site_visits/components/ScheduleVisitModal";
import { VisitCard } from "src/features/site_visits/components/VisitCard";
import { STATUS_META } from "src/features/site_visits/constants";

function startOfWeek(date) {
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7; // Monday = 0
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
}

function isoDate(d) {
  return d.toISOString().slice(0, 10);
}

function dayLabel(iso) {
  const d = new Date(iso);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((new Date(iso).setHours(0, 0, 0, 0) - today) / 86400000);
  const weekday = d.toLocaleDateString([], { weekday: "long", day: "numeric", month: "short" });
  if (diff === 0) return `Today · ${weekday}`;
  if (diff === 1) return `Tomorrow · ${weekday}`;
  return weekday;
}

export default function SiteVisitsPage() {
  const { can } = useAuth();
  const { toast } = useToast();
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [scheduleOpen, setScheduleOpen] = useState(false);

  const weekEnd = useMemo(() => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 6);
    return d;
  }, [weekStart]);

  const load = useCallback(() => {
    setLoading(true);
    fetchCalendar({
      from: isoDate(weekStart),
      to: isoDate(weekEnd),
      status: statusFilter || undefined,
    })
      .then((data) => setVisits(data.site_visits || []))
      .catch(() => toast({ variant: "destructive", title: "Couldn't load site visits" }))
      .finally(() => setLoading(false));
  }, [weekStart, weekEnd, statusFilter, toast]);

  useEffect(() => {
    load();
  }, [load]);

  // Group visits by calendar day.
  const grouped = useMemo(() => {
    const map = {};
    visits.forEach((v) => {
      const key = (v.scheduled_at || "").slice(0, 10);
      (map[key] ||= []).push(v);
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [visits]);

  function handleChanged(updated) {
    if (!updated) return load();
    setVisits((prev) => prev.map((v) => (v.id === updated.id ? updated : v)));
  }

  const canCreate = can("site_visits", "create");

  return (
    <AppShell>
      <div className="space-y-5 px-8 py-7">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3.5">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white shadow-sm">
              <MapPin className="h-5 w-5 text-slate-700" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-slate-900">Site Visits</h1>
              <p className="text-[13px] text-slate-400">
                Schedule visits, verify check-ins, and capture feedback.
              </p>
            </div>
          </div>
          {canCreate && (
            <Button onClick={() => setScheduleOpen(true)}>
              <Plus className="mr-1.5 h-4 w-4" /> Schedule visit
            </Button>
          )}
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-2.5 shadow-sm">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => setWeekStart(startOfWeek(new Date(weekStart.getTime() - 7 * 86400000)))}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
              <CalendarDays className="h-4 w-4 text-slate-400" />
              {weekStart.toLocaleDateString([], { day: "numeric", month: "short" })} –{" "}
              {weekEnd.toLocaleDateString([], { day: "numeric", month: "short" })}
            </div>
            <Button variant="outline" size="icon" onClick={() => setWeekStart(startOfWeek(new Date(weekStart.getTime() + 7 * 86400000)))}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setWeekStart(startOfWeek(new Date()))}>
              This week
            </Button>
          </div>

          <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {Object.entries(STATUS_META).map(([key, meta]) => (
                <SelectItem key={key} value={key}>
                  {meta.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Agenda */}
        {loading ? (
          <div className="py-16 text-center text-sm text-slate-400">Loading…</div>
        ) : grouped.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 py-16 text-center">
            <MapPin className="mx-auto h-8 w-8 text-slate-300" />
            <p className="mt-2 text-sm text-slate-500">No site visits this week.</p>
            {canCreate && (
              <Button variant="outline" className="mt-3" onClick={() => setScheduleOpen(true)}>
                <Plus className="mr-1.5 h-4 w-4" /> Schedule one
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {grouped.map(([day, dayVisits]) => (
              <div key={day}>
                <h2 className="mb-2 text-[13px] font-semibold uppercase tracking-wide text-slate-400">
                  {dayLabel(day)}
                </h2>
                <div className="space-y-2.5">
                  {dayVisits.map((v) => (
                    <VisitCard key={v.id} visit={v} onChanged={handleChanged} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ScheduleVisitModal open={scheduleOpen} onOpenChange={setScheduleOpen} onScheduled={() => load()} />
    </AppShell>
  );
}
